import React, { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle, Trophy, Users, BookOpen, Clock } from 'lucide-react';
import PrivacyConsent from '../forms/PrivacyConsent';
import { facebookPixelEvents } from '../../utils/facebookPixel';
import sendGridService from '../../lib/api/sendGridService';
import benjamin from '../../assets/Benjamin.jpeg';
import { useOnlineClasses } from '../../contexts/OnlineClassContext';
import { OnlineClass } from '../../types/api';

interface FreeClassRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  classDetails?: {
    title: string;
    date: string;
    time: string;
    id?: number;
  };
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  homeschoolType: string;
  studentAge: string;
  homeschoolRole: string;
  Free_Class_Date: string;
}

interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const sendGridListId = import.meta.env.VITE_SENDGRID_LIST_HS_SUMMER25_ID;

export default function FreeClassRegistrationModal({ 
  isOpen, 
  onClose, 
  classDetails 
}: FreeClassRegistrationModalProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    homeschoolType: '',
    studentAge: '',
    homeschoolRole: '',
    Free_Class_Date: ''
  });
  const [utmParams, setUtmParams] = useState<UTMParams>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [availableFreeClasses, setAvailableFreeClasses] = useState<OnlineClass[]>([]);

  // Use the online classes hook
  const { upcomingClasses, loading: classesLoading } = useOnlineClasses();

  // Filter and set available free classes
  useEffect(() => {
    if (upcomingClasses.length > 0) {
      const freeClasses = upcomingClasses.filter(cls => 
        cls.price === 0 && 
        cls.startDate > new Date() && 
        (!cls.maxStudents || !cls.currentEnrollment || cls.currentEnrollment < cls.maxStudents)
      );
      setAvailableFreeClasses(freeClasses);
    }
  }, [upcomingClasses]);

  // Capture UTM parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utms: UTMParams = {};
    
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        utms[param as keyof UTMParams] = value;
      }
    });
    
    setUtmParams(utms);
    
    if (Object.keys(utms).length > 0) {
      sessionStorage.setItem('utmParams', JSON.stringify(utms));
    } else {
      const storedUtms = sessionStorage.getItem('utmParams');
      if (storedUtms) {
        setUtmParams(JSON.parse(storedUtms));
      }
    }
  }, []);

  // Track modal open
  useEffect(() => {
    if (isOpen) {
      // Track form view
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'form_view', {
          form_type: 'free_class_registration',
          class_id: classDetails?.id,
          class_title: classDetails?.title
        });
      }

      // Facebook Pixel form view tracking
      facebookPixelEvents.viewContent({
        content_name: `Free Class Registration - ${classDetails?.title || 'VBC Online'}`,
        content_category: 'free_class_registration',
        content_type: 'form_view',
        content_id: classDetails?.id?.toString()
      });
    }
  }, [isOpen, classDetails]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        homeschoolType: '',
        studentAge: '',
        homeschoolRole: '',
        Free_Class_Date: ''
      });
      setError('');
      setShowSuccess(false);
    }
  }, [isOpen]);

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1 && formData.homeschoolType && formData.studentAge && formData.homeschoolRole && formData.Free_Class_Date) {
      setStep(2);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      handleNext(e);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      if (!sendGridListId) {
        throw new Error('SendGrid List ID is not configured. Please set VITE_SENDGRID_LIST_HS_SUMMER25_ID environment variable.');
      }

      // Track form submission with Facebook Pixel
      facebookPixelEvents.lead({
        content_name: `Free Class Registration Submission - ${classDetails?.title || 'VBC Online'}`,
        content_category: 'free_class_registration',
        content_type: 'form_submission',
        content_id: classDetails?.id?.toString()
      });

      // Extract just the date from the combined value (classId-date format)
      const dateOnly = formData.Free_Class_Date.includes('-') 
        ? formData.Free_Class_Date.split('-').slice(1).join('-') // Remove class ID, keep date
        : formData.Free_Class_Date;

      // Prepare form data with the correct date format
      const { Free_Class_Date, ...restFormData } = formData;
      const submitData = {
        ...restFormData,
        Free_Class_Date: dateOnly, // Send only the date to SendGrid
      };

      // Submit to SendGrid with free class specific data
      const sendGridSuccess = await sendGridService.addFacebookFormContact(
        submitData,
        {
          ...utmParams,
          utm_content: `free_class_${classDetails?.id || 'unknown'}`
        },
        sendGridListId
      );

      if (sendGridSuccess) {
        // Track successful registration
        facebookPixelEvents.completeRegistration({
          content_name: `Free Class Registration Complete - ${classDetails?.title || 'VBC Online'}`,
          content_category: 'free_class_registration',
          content_type: 'registration_success',
          content_id: classDetails?.id?.toString()
        });
        
        setShowSuccess(true);
        setIsSubmitting(false); // Reset submitting state on success
        
        // Auto close after success
        setTimeout(() => {
          onClose();
        }, 8000);
      } else {
        throw new Error('Registration failed');
      }
    } catch (err: any) {
      console.error('Free class registration error:', err);
      setError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-green-500 to-emerald-600 px-8 py-6 text-white">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 overflow-hidden rounded-full border-4 border-white/20 shadow-lg">
              <img
                src={benjamin}
                alt="Benjamin Ryan"
                className="w-full h-full object-cover object-[center_18%] scale-110"
              />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-1">
                🎉 Reserve Your FREE Spot
              </h2>
              <p className="text-green-50">
                {classDetails?.title || 'VBC Online Business Class'}
              </p>
              {classDetails?.date && classDetails?.time && (
                <p className="text-green-100 text-sm mt-1">
                  {classDetails.date} • {classDetails.time}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="p-8">
          {showSuccess ? (
            <div className="text-center">
              <div className="mb-6 text-green-600">
                <CheckCircle className="w-20 h-20 mx-auto" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">
                🎊 You're signed up!
              </h3>
              <p className="text-gray-600 mb-4 text-lg">
                Congratulations! We have received your registration.
              </p>
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-green-900 mb-3">What happens next?</h4>
                <ul className="text-left space-y-2 text-green-800">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                    You'll receive a confirmation email with class details, please check your spam folder if you don't see it.
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                    The email will also contain Access links as well as optional pre-class materials.
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                    Join our community for updates and tips
                  </li>
                </ul>
              </div>
              <p className="text-gray-500 text-sm">
                This window will close automatically in a few seconds...
              </p>
            </div>
          ) : (
            <>
              {/* Free Class Benefits */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  What's Included in Your FREE Class
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <Trophy className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Full Business Simulation</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Live Team Competition</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <BookOpen className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Certificate of Completion</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <Clock className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">3 Hours of Learning</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Registration Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {step === 1 ? (
                  <>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Step 1: Tell us about your homeschool setup
                    </h3>
                    
                    <div>
                      <label htmlFor="homeschoolRole" className="block text-sm font-medium text-gray-700 mb-2">
                        Your Role <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="homeschoolRole"
                        name="homeschoolRole"
                        value={formData.homeschoolRole}
                        onChange={handleChange}
                        required
                        className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 p-3"
                      >
                        <option value="">Select your role</option>
                        <option value="homeschool_parent">Homeschool Parent</option>
                        <option value="homeschool_teacher">Homeschool Educator</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="homeschoolType" className="block text-sm font-medium text-gray-700 mb-2">
                        Type of Homeschool <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="homeschoolType"
                        name="homeschoolType"
                        value={formData.homeschoolType}
                        onChange={handleChange}
                        required
                        className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 p-3"
                      >
                        <option value="">Select type</option>
                        <option value="individual_household">Individual Household</option>
                        <option value="homeschool_coop">Homeschool Cooperative</option>
                        <option value="micro_schools">Micro School</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="studentAge" className="block text-sm font-medium text-gray-700 mb-2">
                        Student Age <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="studentAge"
                        name="studentAge"
                        value={formData.studentAge}
                        onChange={handleChange}
                        required
                        className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 p-3"
                      >
                        <option value="">Select age range</option>
                        <option value="middle_school">Middle School</option>
                        <option value="high_school">High School</option>
                        <option value="m_h_school">Middle & High School</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="Free_Class_Date" className="block text-sm font-medium text-gray-700 mb-2">
                        Date of the Free Class <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="Free_Class_Date"
                        name="Free_Class_Date"
                        value={formData.Free_Class_Date}
                        onChange={handleChange}
                        required
                        disabled={classesLoading}
                        className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 p-3 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">
                          {classesLoading ? "Loading classes..." : "Select a free class date"}
                        </option>
                        {availableFreeClasses.map(cls => {
                          const classDate = cls.startDate.toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          });
                          const classTime = cls.startDate.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          });
                          return (
                            <option key={cls.id} value={`${cls.id}-${cls.startDate.toISOString().split('T')[0]}`}>
                              {cls.title} - {classDate} at {classTime}
                            </option>
                          );
                        })}
                      </select>
                      {availableFreeClasses.length === 0 && !classesLoading && (
                        <p className="text-sm text-gray-500 mt-2">
                          No free classes available at the moment. Please check back later.
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={availableFreeClasses.length === 0 || classesLoading}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {classesLoading ? 'Loading...' : 'Next Step →'}
                    </button>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Step 2: Your contact information
                    </h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          id="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 p-3"
                          placeholder="First name"
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          id="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 p-3"
                          placeholder="Last name"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 p-3"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number <span className="text-gray-500 text-sm">(optional)</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        id="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500 p-3"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    {error && (
                      <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                        {error}
                      </div>
                    )}

                    <div>
                      <PrivacyConsent 
                        onConsentChange={(field, value) => console.log(field, value)}
                        required={true}
                      />
                    </div>

                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors"
                      >
                        ← Back
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Registering...
                          </span>
                        ) : (
                          '🎯 Reserve My FREE Spot'
                        )}
                      </button>
                    </div>

                    <p className="text-xs text-gray-500 text-center">
                      By registering, you agree to our{' '}
                      <a href="/privacy-policy" className="text-green-600 hover:underline">
                        Privacy Policy
                      </a>. No credit card required.
                    </p>
                  </>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
} 